// src/Hooks/usePushNotifications.js
import { useEffect, useCallback } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import client from '../API';
import { Network } from '@capacitor/network';

export default function usePushNotifications() {
  const registerToken = useCallback(async (token) => {
    if (!token) {
      console.error('[Push] Empty token');
      return;
    }

    console.info('[Push] APNs token:', token);
    window._apnsToken = token; // Expose globally
    await Preferences.set({ key: 'apns_token', value: token });

    try {
      // Check network status first
      const status = await Network.getStatus();
      console.info('[Push] Network status:', status);
      
      if (!status.connected) {
        console.warn('[Push] No network connection, token will be sent later');
        return;
      }

      await client.notifications.registerDeviceToken({
        device_token: token,
        platform: 'ios',
      });
      console.info('[Push] Token sent to backend');
    } catch (err) {
      console.error('[Push] registerDeviceToken failed', err);
    }
  }, []);

  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') {
      console.info('[Push] iOS only');
      return;
    }

    const setup = async () => {
      try {
        console.info('[Push] Starting setup...');
        
        // Check existing permissions
        const { receive } = await PushNotifications.checkPermissions();
        console.info('[Push] Current permissions:', { receive });
        
        if (receive === 'prompt') {
          console.info('[Push] Requesting permissions...');
          const { receive: newStatus } = await PushNotifications.requestPermissions();
          console.info('[Push] Permission result:', newStatus);
          
          if (newStatus !== 'granted') {
            console.warn('[Push] Permission denied');
            return;
          }
        } else if (receive !== 'granted') {
          console.warn('[Push] Permission previously denied');
          return;
        }

        // Add listeners with more detailed logging
        const registrationListener = await PushNotifications.addListener('registration', ({ value }) => {
          console.info('[Push] ✅ Registration successful! Token:', value);
          registerToken(value);
        });

        const errorListener = await PushNotifications.addListener('registrationError', (err) => {
          console.error('[Push] ❌ Registration error:', err);
          
          // Common error handling
          if (err.error?.includes('no valid')) {
            console.error('[Push] Provisioning profile issue - check capabilities');
          } else if (err.error?.includes('network')) {
            console.error('[Push] Network issue - try again later');
          }
        });

        // Add push received listeners for completeness
        const pushReceivedListener = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.info('[Push] Notification received:', notification);
        });

        const pushActionListener = await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          console.info('[Push] Notification action performed:', notification);
        });

        // Register for APNs
        console.info('[Push] Initiating registration...');
        await PushNotifications.register();
        console.info('[Push] Registration initiated');

        // Wait a bit to see if registration completes
        setTimeout(async () => {
          const { value: stored } = await Preferences.get({ key: 'apns_token' });
          if (!stored) {
            console.warn('[Push] No token received after 5 seconds - check setup');
          }
        }, 5000);

        // Resend cached token if available
        const { value: stored } = await Preferences.get({ key: 'apns_token' });
        if (stored) {
          window._apnsToken = stored;
          console.info('[Push] Resending stored token:', stored);
          await registerToken(stored);
        }

        return () => {
          console.info('[Push] Cleaning up listeners');
          registrationListener.remove();
          errorListener.remove();
          pushReceivedListener.remove();
          pushActionListener.remove();
        };

      } catch (e) {
        console.error('[Push] Setup error', e);
      }
    };

    const cleanup = setup();
    
    return () => {
      cleanup.then(cleanupFn => cleanupFn?.());
    };
  }, [registerToken]);
}